import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Combobox, type ComboboxOption } from '../../components/Combobox';
import { useDebounced } from '../../lib/useDebounced';
import { useTicketLookup } from './queries';

interface TicketComboboxProps {
  id?: string;
  ariaLabel?: string;
  value: ComboboxOption | null;
  onChange: (option: ComboboxOption | null) => void;
  /** Das Ticket, an dem man gerade steht -- es ist nie sein eigenes Ziel. */
  excludeId?: string;
  placeholder?: string;
  disabled?: boolean;
}

/**
 * Ein Zielticket waehlen -- zum Zusammenfuehren, Verknuepfen, Unterordnen.
 *
 * **Nummer oder Betreff, auch teilweise.** "57" findet "T-2026-000057",
 * "drucker" findet den Drucker am Empfang. Vorher war das ein Feld, in das
 * man die vollstaendige Nummer abschreiben musste; die Nummer wird weiter
 * verstanden, sie muss nur nicht mehr vollstaendig sein.
 *
 * Kunde und Stand stehen in der Zeile: Zwei Tickets "Drucker druckt nicht"
 * unterscheiden sich an ihnen, und ein geschlossenes Ticket ist als Ziel
 * selten gemeint.
 */
export function TicketCombobox({
  id,
  ariaLabel,
  value,
  onChange,
  excludeId,
  placeholder,
  disabled,
}: TicketComboboxProps) {
  const { t } = useTranslation();
  const [term, setTerm] = useState('');
  // Erst beim ersten Oeffnen fragen -- siehe ``useCustomerLookup``.
  const [opened, setOpened] = useState(false);
  const search = useDebounced(term);
  const tickets = useTicketLookup(search, opened);

  const options = (tickets.data ?? [])
    .filter((ticket) => ticket.id !== excludeId)
    .map((ticket) => ({
      id: ticket.id,
      label: `${ticket.ticket_number} · ${ticket.title}`,
      meta: ticket.customer.name,
      hint: ticket.state.name,
    }));

  return (
    <Combobox
      id={id}
      ariaLabel={ariaLabel}
      value={value}
      onChange={onChange}
      options={options}
      onSearch={(next) => {
        setOpened(true);
        setTerm(next);
      }}
      loading={tickets.isFetching}
      placeholder={placeholder ?? t('combobox.searchTicket')}
      disabled={disabled}
    />
  );
}
